import { makeStyles } from "@material-ui/core";

// Progress / quick-nav / badge / error-summary keys were moved here verbatim
// from Add_Loan.styles.js (LLD §3.7). This is now the only copy.
const useStyles = makeStyles((theme) => ({
  progressHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 1),
    marginBottom: theme.spacing(1),
    flexShrink: 0,
    position: "relative",
    zIndex: 4,
    backgroundColor: "var(--color-background-paper)",
    boxShadow: "0 8px 14px -10px rgba(0, 0, 0, 0.35)",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.25),
    },
  },
  // Selected by the `sticky` prop rather than a host className, so it does not
  // depend on JSS sheet injection order (LLD §3.7 trap).
  progressHeaderSticky: {
    position: "sticky",
    top: 0,
    zIndex: 4,
  },
  progressRingWrap: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  progressRing: {
    position: "relative",
    width: 40,
    height: 40,
    display: "inline-flex",
    flexShrink: 0,
  },
  progressRingTrack: {
    color: "var(--total_row_boldbackground)",
    position: "absolute",
    left: 0,
    top: 0,
  },
  progressRingValue: {
    color: "var(--color-primary-text)",
    position: "absolute",
    left: 0,
    top: 0,
    "& .MuiCircularProgress-circle": {
      strokeLinecap: "round",
      transition: "stroke-dashoffset 0.6s ease",
    },
  },
  progressRingLabel: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  progressRingPercent: {
    fontSize: "0.625rem",
    fontWeight: 700,
    color: "var(--color-text-primary)",
  },
  progressMetaText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  progressTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    lineHeight: 1.25,
    color: "var(--color-text-primary)",
  },
  progressSubtitle: {
    fontSize: "0.75rem",
    color: "var(--color-text-secondary)",
  },
  quickNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    overflowX: "auto",
    overflowY: "hidden",
    flex: 1,
    minWidth: 0,
    paddingTop: 4,
    paddingBottom: 4,
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 auto",
      width: "100%",
      WebkitOverflowScrolling: "touch",
    },
    "&::-webkit-scrollbar": {
      height: 4,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "var(--total_row_boldbackground)",
      borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    },
  },
  quickNavItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
    border: "1px solid var(--total_row_boldbackground)",
    backgroundColor: "var(--color-background-paper)",
    borderRadius: 999,
    padding: theme.spacing(0.5, 1.25),
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    transition:
      "border-color 0.18s ease, color 0.18s ease, transform 0.18s ease, background-color 0.18s ease",
    "&:hover": {
      borderColor: "var(--color-primary-main)",
      color: "var(--color-primary-text)",
      transform: "translateY(-1px)",
    },
  },
  quickNavIcon: {
    display: "inline-flex",
    alignItems: "center",
  },
  quickNavComplete: {
    borderColor:
      "color-mix(in srgb, var(--color-primary-main) 40%, transparent)",
    color: "var(--color-primary-text)",
    backgroundColor:
      "color-mix(in srgb, var(--color-primary-main) 8%, transparent)",
    "&:hover": {
      borderColor: "var(--color-primary-main)",
      color: "var(--color-primary-text)",
    },
  },
  quickNavError: {
    borderColor: "rgba(244, 67, 54, 0.4)",
    color: "var(--error-color)",
    backgroundColor: "rgba(244, 67, 54, 0.08)",
    "&:hover": {
      borderColor: "var(--error-color)",
      color: "var(--error-color)",
    },
  },
  sectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.7rem",
    fontWeight: 700,
    lineHeight: 1,
    padding: theme.spacing(0.5, 1),
    borderRadius: 999,
    whiteSpace: "nowrap",
    marginLeft: "auto",
    flexShrink: 0,
  },
  // The five inline style objects from Add_Loan's accordion badge.
  badgeLocked: {
    color: "var(--color-text-secondary)",
    backgroundColor: "var(--total_row_background)",
  },
  badgeError: {
    color: "var(--error-color)",
    backgroundColor: "rgba(244, 67, 54, 0.12)",
  },
  badgeComplete: {
    color: "var(--color-primary-text)",
    backgroundColor:
      "color-mix(in srgb, var(--color-primary-main) 12%, transparent)",
  },
  badgePartial: {
    color: "var(--color-text-secondary)",
    backgroundColor: "var(--total_row_background)",
  },
  badgeOptional: {
    color: "var(--color-text-secondary)",
    backgroundColor: "var(--total_row_background)",
  },
  errorSummaryBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderRadius: "calc(12px * var(--app-radius-scale, 1))",
    border: "1px solid rgba(244, 67, 54, 0.4)",
    backgroundColor: "rgba(244, 67, 54, 0.06)",
  },
  errorSummaryIcon: {
    color: "var(--error-color)",
    fontSize: "1.25rem",
    flexShrink: 0,
    marginTop: 2,
  },
  // Same value as Add_Loan's draftBannerContent, which the banner body used to
  // borrow. Keep it verbatim — anything else changes the loan banner's width.
  errorSummaryContent: {
    minWidth: 0,
    flex: 1,
  },
  errorSummaryTitle: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
    color: "var(--error-color)",
  },
  errorSummaryList: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  errorSummaryItem: {
    border: "1px solid rgba(244, 67, 54, 0.35)",
    background: "rgba(244, 67, 54, 0.04)",
    padding: theme.spacing(0.25, 1),
    margin: 0,
    borderRadius: 999,
    textAlign: "left",
    cursor: "pointer",
    fontSize: "0.75rem",
    lineHeight: 1.5,
    color: "var(--color-text-secondary)",
    transition: "background 0.15s ease, color 0.15s ease",
    "&:hover": {
      color: "var(--error-color)",
      background: "rgba(244, 67, 54, 0.12)",
      borderColor: "var(--error-color)",
    },
  },
  // Pill for an error with no field to focus. Declared after errorSummaryItem
  // so its hover reset wins at equal specificity.
  errorSummaryItemStatic: {
    cursor: "default",
    "&:hover": {
      color: "var(--color-text-secondary)",
      background: "rgba(244, 67, 54, 0.04)",
      borderColor: "rgba(244, 67, 54, 0.35)",
    },
  },
}));

export default useStyles;
